import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ZoomIn, ZoomOut, Maximize2, Move } from "lucide-react";

// Palette for distinct piece strokes (subtle, technical)
function ptsToPath(points, H) {
  if (!points || points.length === 0) return "";
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(4)} ${(H - p[1]).toFixed(4)}`)
    .join(" ");
  return `${d} Z`;
}

function lineToPath(points, H) {
  if (!points || points.length < 2) return "";
  return points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(4)} ${(H - p[1]).toFixed(4)}`)
    .join(" ");
}

export default function DxfCanvas({ data, highlightKey, setHighlightKey, showLabels }) {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const W = data.bbox.w;
  const H = data.bbox.h;
  const PAD = Math.max(W, H) * 0.06;

  const fit = useMemo(
    () => ({ x: -PAD, y: -PAD, w: W + PAD * 2, h: H + PAD * 2 }),
    [W, H, PAD]
  );
  const [vb, setVb] = useState(fit);
  const [panning, setPanning] = useState(false);
  const panState = useRef(null);
  const [cursor, setCursor] = useState(null); // {x,y,name,size}

  useEffect(() => setVb(fit), [fit]);

  const clientToWorld = useCallback((clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = (clientX - rect.left) / rect.width;
    const py = (clientY - rect.top) / rect.height;
    return { x: vb.x + px * vb.w, y: vb.y + py * vb.h };
  }, [vb]);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const worldX = vb.x + px * vb.w;
    const worldY = vb.y + py * vb.h;
    const factor = e.deltaY > 0 ? 1.12 : 1 / 1.12;
    const newW = Math.min(Math.max(vb.w * factor, W * 0.02), (W + PAD * 2) * 6);
    const newH = newW * (vb.h / vb.w);
    setVb({ x: worldX - px * newW, y: worldY - py * newH, w: newW, h: newH });
  }, [vb, W, PAD]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    setPanning(true);
    panState.current = { sx: e.clientX, sy: e.clientY, vb: { ...vb } };
  };
  const onMouseMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    if (panning && panState.current) {
      const dx = ((e.clientX - panState.current.sx) / rect.width) * panState.current.vb.w;
      const dy = ((e.clientY - panState.current.sy) / rect.height) * panState.current.vb.h;
      setVb({ ...panState.current.vb, x: panState.current.vb.x - dx, y: panState.current.vb.y - dy });
    }
    if (cursor) setCursor((c) => ({ ...c, sx: e.clientX - rect.left, sy: e.clientY - rect.top }));
  };
  const endPan = () => { setPanning(false); panState.current = null; };

  const zoomBtn = (dir) => {
    const cx = vb.x + vb.w / 2;
    const cy = vb.y + vb.h / 2;
    const factor = dir === "in" ? 1 / 1.3 : 1.3;
    const newW = Math.min(Math.max(vb.w * factor, W * 0.02), (W + PAD * 2) * 6);
    const newH = newW * (vb.h / vb.w);
    setVb({ x: cx - newW / 2, y: cy - newH / 2, w: newW, h: newH });
  };

  const labelSize = vb.w * 0.011;
  const zoomPct = Math.round(((W + PAD * 2) / vb.w) * 100);

  const accentPieces = data.pieces;

  return (
    <div ref={wrapRef} className="relative w-full h-full overflow-hidden blueprint-grid select-none">
      <svg
        ref={svgRef}
        data-testid="dxf-canvas-svg"
        className="w-full h-full"
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ cursor: panning ? "grabbing" : "grab" }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endPan}
        onMouseLeave={() => { endPan(); setCursor(null); }}
      >
        {/* fabric / marker rectangle */}
        <rect
          x={0} y={0} width={W} height={H}
          fill="hsl(var(--surface))"
          stroke="hsl(var(--foreground))"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />

        {accentPieces.map((pc) => {
          const key = `${pc.name}||${pc.size || "-"}`;
          const active = highlightKey === key;
          return (
            <g
              key={pc.uid}
              data-testid={`piece-${pc.uid}`}
              onMouseEnter={(e) => {
                setHighlightKey(key);
                const rect = svgRef.current.getBoundingClientRect();
                const xs = pc.boundary.map((p) => p[0]);
                const ys = pc.boundary.map((p) => p[1]);
                const w = (Math.max(...xs) - Math.min(...xs)) * 100;
                const h = (Math.max(...ys) - Math.min(...ys)) * 100;
                setCursor({ sx: e.clientX - rect.left, sy: e.clientY - rect.top, name: pc.name, size: pc.size, comment: pc.comment, w, h });
              }}
              onMouseLeave={() => { setHighlightKey(null); setCursor(null); }}
              style={{ cursor: "pointer" }}
            >
              <path
                d={ptsToPath(pc.boundary, H)}
                fill={active ? "hsl(var(--accent-hl) / 0.22)" : "hsl(var(--primary) / 0.04)"}
                stroke={active ? "hsl(var(--accent-hl))" : "hsl(var(--foreground))"}
                strokeWidth={active ? 2 : 1}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
              {pc.internals.map((ln, i) => (
                <path
                  key={i}
                  d={lineToPath(ln, H)}
                  fill="none"
                  stroke={active ? "hsl(var(--accent-hl))" : "hsl(var(--muted-foreground))"}
                  strokeWidth={0.75}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {showLabels && (
                <text
                  x={pc.centroid[0]}
                  y={H - pc.centroid[1]}
                  fontSize={labelSize}
                  fill={active ? "hsl(var(--accent-hl))" : "hsl(var(--foreground))"}
                  fontFamily="'JetBrains Mono', monospace"
                  fontWeight="500"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ pointerEvents: "none" }}
                >
                  {pc.name}{pc.size ? ` · ${pc.size}` : ""}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* cursor tooltip */}
      {cursor && cursor.name && (
        <div
          className="absolute z-20 px-2 py-1 bg-foreground text-background text-xs mono pointer-events-none whitespace-nowrap"
          style={{ left: cursor.sx + 14, top: cursor.sy + 14 }}
        >
          <div>{cursor.name}{cursor.size && cursor.size !== "-" ? ` / ${cursor.size}` : ""}{cursor.comment ? ` · ${cursor.comment}` : ""}</div>
          {cursor.w != null && (
            <div className="opacity-70">{cursor.w.toFixed(1).replace(".", ",")} × {cursor.h.toFixed(1).replace(".", ",")} cm</div>
          )}
        </div>
      )}

      {/* toolbar */}
      <div className="absolute left-4 bottom-4 z-10 flex flex-col border border-border bg-card divide-y divide-border">
        <button data-testid="zoom-in-btn" onClick={() => zoomBtn("in")} className="p-2.5 hover:bg-accent transition-colors" title="Aproximar">
          <ZoomIn className="w-4 h-4" />
        </button>
        <button data-testid="zoom-out-btn" onClick={() => zoomBtn("out")} className="p-2.5 hover:bg-accent transition-colors" title="Afastar">
          <ZoomOut className="w-4 h-4" />
        </button>
        <button data-testid="zoom-reset-btn" onClick={() => setVb(fit)} className="p-2.5 hover:bg-accent transition-colors" title="Ajustar à janela">
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      {/* zoom indicator */}
      <div className="absolute right-4 bottom-4 z-10 flex items-center gap-2 border border-border bg-card px-3 py-1.5 text-xs mono text-muted-foreground">
        <Move className="w-3.5 h-3.5" />
        <span data-testid="zoom-indicator">{zoomPct}%</span>
      </div>
    </div>
  );
}
